import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from '../utils/toast';
import { Loader2, Plus, X, Upload, Link, Trash2, Play } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const writePreviewWindowContent = (previewWindow, html) => {
  previewWindow.document.open();
  previewWindow.document.write(html);
  previewWindow.document.close();
};

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const isPreviewReference = (value) => {
  if (!value || typeof value !== 'string') return false;
  const trimmedValue = value.trim();
  if (!trimmedValue) return false;
  if (trimmedValue.startsWith('http://') || trimmedValue.startsWith('https://')) return true;
  return trimmedValue.includes('/') || trimmedValue.includes('\\');
};

const getPreviewSource = (primaryValue, fallbackValue) => {
  if (isPreviewReference(primaryValue)) return primaryValue.trim();
  if (isPreviewReference(fallbackValue)) return fallbackValue.trim();
  return '';
};

const inferVideoMimeType = (value = '') => {
  const normalizedValue = value.split('?')[0].toLowerCase();

  if (normalizedValue.endsWith('.webm')) return 'video/webm';
  if (normalizedValue.endsWith('.ogg') || normalizedValue.endsWith('.ogv')) return 'video/ogg';
  if (normalizedValue.endsWith('.mov')) return 'video/quicktime';
  if (normalizedValue.endsWith('.m4v')) return 'video/x-m4v';

  return 'video/mp4';
};

const writePreviewError = (previewWindow, title, message) => {
  writePreviewWindowContent(
    previewWindow,
    `
      <html>
        <head><title>${escapeHtml(title)}</title></head>
        <body style="margin:0;font-family:Arial,sans-serif;background:#111827;color:#f9fafb;display:flex;align-items:center;justify-content:center;min-height:100vh;padding:24px;box-sizing:border-box;">
          <div style="max-width:560px;background:#1f2937;border:1px solid #374151;border-radius:12px;padding:24px;box-shadow:0 10px 30px rgba(0,0,0,0.35);">
            <h1 style="margin:0 0 12px;font-size:20px;">${escapeHtml(title)}</h1>
            <p style="margin:0;font-size:14px;line-height:1.6;color:#d1d5db;">${escapeHtml(message)}</p>
          </div>
        </body>
      </html>
    `
  );
};

const resolvePreviewUrl = async (fileValue) => {
  if (!fileValue || typeof fileValue !== 'string' || !fileValue.trim()) {
    throw new Error('Preview source is missing for this file. Upload the file again if the chapter was saved without a valid media reference.');
  }

  const normalizedFileValue = fileValue.trim();

  if (normalizedFileValue.startsWith('http://') || normalizedFileValue.startsWith('https://')) {
    return normalizedFileValue;
  }

  const token = localStorage.getItem('token');
  let response;

  try {
    response = await axios.post(
      `${API}/s3/signed-url`,
      { s3_key: normalizedFileValue, expiration: 3600 },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (error) {
    throw new Error(error?.response?.data?.detail || 'Failed to generate signed URL for preview.');
  }

  if (!response.data?.signed_url) {
    throw new Error('Signed URL response was empty.');
  }

  return response.data.signed_url;
};

const openVideoPreviewWindow = (previewWindow, videoUrl, title = 'Video Preview') => {
  const safeVideoUrl = escapeHtml(videoUrl);
  const safeTitle = escapeHtml(title);
  const mimeType = inferVideoMimeType(videoUrl);
  writePreviewWindowContent(
    previewWindow,
    `
      <html>
        <head><title>${safeTitle}</title></head>
        <body style="margin:0; padding:0; background:#000; display:flex; align-items:center; justify-content:center; height:100vh;">
          <div id="preview-error" style="display:none;max-width:560px;padding:24px;color:#f9fafb;font-family:Arial,sans-serif;text-align:left;line-height:1.6;">
            <div style="background:#111827;border:1px solid #374151;border-radius:12px;padding:24px;">
              <h1 style="margin:0 0 12px;font-size:20px;">${safeTitle}</h1>
              <p style="margin:0;color:#d1d5db;">Unable to load this preview. The video file may be unavailable, expired, or in an unsupported format.</p>
            </div>
          </div>
          <video id="preview-player" controls autoplay playsinline style="max-width:100%; max-height:100%;">
            <source src="${safeVideoUrl}" type="${mimeType}">
            Your browser does not support the video tag.
          </video>
          <script>
            const player = document.getElementById('preview-player');
            const errorPanel = document.getElementById('preview-error');
            player.addEventListener('error', function () {
              player.style.display = 'none';
              errorPanel.style.display = 'block';
            });
          </script>
        </body>
      </html>
    `
  );
};

const AddTrainingContentPage = () => {
  const navigate = useNavigate();
  const { contentId } = useParams();
  const isEdit = Boolean(contentId);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  
  // Get user info to check role
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isExlvaAdmin = ['Super Admin', 'Exlva Admin', 'ExlvaAdmin', 'ExlvaAdminLite', 'Exlva Admin Lite'].includes(user?.role);
  
  // Subchapter metadata (IDs and summary content) - only used in edit mode
  const [sectionMeta, setSectionMeta] = useState([]); // [{id, summary_video, summary_pdf}, ...]
  const [summaryUploading, setSummaryUploading] = useState({});
  const fetchedThumbnailKeys = useRef(new Set()); // Track which S3 keys have already been fetched

  // Language tabs management
  const [activeLangTabs, setActiveLangTabs] = useState(['English']);
  const [currentTab, setCurrentTab] = useState('English');
  const [showAddLangDialog, setShowAddLangDialog] = useState(false);
  const [newLangName, setNewLangName] = useState('');
  
  // Available languages for suggestions
  const availableLanguages = ['Hindi', 'Kannada', 'Tamil', 'Telugu', 'Malayalam', 'Marathi', 'Bengali', 'Gujarati', 'Punjabi', 'Urdu'];

  // Training type options
  const trainingTypes = [
    { value: 'online', label: 'Online', description: 'Video/PDF content with optional assessment' },
    { value: 'offline', label: 'Offline', description: 'In-person training, completed via attendance' },
    { value: 'hybrid', label: 'Hybrid', description: 'Offline attendance + online assessment required' }
  ];

  // Form data - common fields
  const [formData, setFormData] = useState({
    training_type: 'online',
    content_structure: 'standalone',
    content_type: 'video',
    status: 'draft',
    mapped_question_ids: [],
    passing_percentage: 70
  });

  // Per-language content storage
  const [languageContent, setLanguageContent] = useState({
    'English': {
      title: '',
      description: '',
      thumbnail_url: '',
      thumbnail_file: null,
      thumbnail_preview: '',
      // For standalone
      content_url: '',
      content_file_name: '',
      content_duration: '',
      // For sectioned
      sections: [{
        section_title: '',
        section_description: '',
        content_url: '',
        content_file_name: '',
        content_duration: ''
      }]
    }
  });

  useEffect(() => {
    if (isEdit) {
      fetchContent();
    }
  }, [contentId]);

  // Auto-fetch signed URL for thumbnail preview when we have an S3 key but no preview
  useEffect(() => {
    const currentContent = languageContent[currentTab];
    const thumbKey = currentContent?.thumbnail_url;
    const hasPreview = !!currentContent?.thumbnail_preview;

    if (!thumbKey || hasPreview || thumbKey.startsWith('http') || fetchedThumbnailKeys.current.has(thumbKey)) {
      return;
    }

    // Mark as fetched immediately to prevent duplicate calls
    fetchedThumbnailKeys.current.add(thumbKey);

    const fetchSignedThumbnail = async (key) => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.post(
          `${API}/s3/signed-url`,
          { s3_key: key, expiration: 3600 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (resp.data?.signed_url) {
          setLanguageContent(prev => {
            // Only update if preview is still missing (avoid overwriting user-initiated changes)
            if (prev[currentTab]?.thumbnail_preview) return prev;
            return {
              ...prev,
              [currentTab]: {
                ...prev[currentTab],
                thumbnail_preview: resp.data.signed_url
              }
            };
          });
        }
      } catch (e) {
        console.warn('Failed to fetch thumbnail signed URL', e);
        // Remove from cache so it can be retried on next tab switch
        fetchedThumbnailKeys.current.delete(key);
      }
    };

    fetchSignedThumbnail(thumbKey);
  }, [currentTab]); // Only re-run when tab changes, not on every languageContent change

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const content = response.data;
      
      setFormData({
        training_type: content.training_type || 'online',
        content_structure: content.content_structure,
        content_type: content.content_type || 'video',
        status: content.status,
        mapped_question_ids: content.mapped_question_ids || [],
        passing_percentage: content.passing_percentage || 70
      });
      
      // Build language content from existing data
      const newLangContent = {};
      const langTabs = ['English'];
      
      // Handle title translations
      const titleTranslations = content.title_translations || { 'English': content.title };
      
      // For standalone content
      if (content.content_structure === 'standalone') {
        // Group language versions
        const langVersions = content.language_versions || [];
        
        Object.keys(titleTranslations).forEach(lang => {
          if (!langTabs.includes(lang)) langTabs.push(lang);
          const langVersion = langVersions.find(v => v.language === lang);
          newLangContent[lang] = {
            title: titleTranslations[lang] || '',
            description: lang === 'English' ? content.description : (content.description_translations?.[lang] || ''),
            thumbnail_url: content.thumbnail_url || '',
            thumbnail_file: null,
            thumbnail_preview: '', // Don't set preview initially, let useEffect fetch signed URL
            content_url: langVersion?.file_url || '',
            content_file_name: langVersion?.file_name || '',
            content_duration: langVersion?.content_duration || '',
            sections: [{
              section_title: '',
              section_description: '',
              content_url: '',
              content_file_name: '',
              content_duration: ''
            }]
          };
        });
      } else {
        // For sectioned content
        const sections = content.sections || [];
        
        // Store section metadata (IDs, summary content) for summary upload
        setSectionMeta(sections.map(section => ({
          id: section.id,
          summary_video: section.summary_video || null,
          summary_pdf: section.summary_pdf || null,
          summary_duration: section.summary_video?.summary_duration || section.summary_pdf?.summary_duration || ''
        })));
        
        Object.keys(titleTranslations).forEach(lang => {
          if (!langTabs.includes(lang)) langTabs.push(lang);
          newLangContent[lang] = {
            title: titleTranslations[lang] || '',
            description: lang === 'English' ? content.description : (content.description_translations?.[lang] || ''),
            thumbnail_url: content.thumbnail_url || '',
            thumbnail_file: null,
            thumbnail_preview: '', // Don't set preview initially, let useEffect fetch signed URL
            content_url: '',
            content_file_name: '',
            sections: sections.map(section => {
              const langVersion = section.language_versions?.find(v => v.language === lang);
              return {
                section_title: section.section_title_translations?.[lang] || section.section_title || '',
                section_description: section.section_description || '',
                content_url: langVersion?.file_url || '',
                content_file_name: langVersion?.file_name || '',
                content_duration: section.content_duration || langVersion?.content_duration || ''
              };
            })
          };
        });
      }
      
      // Ensure English exists
      if (!newLangContent['English']) {
        newLangContent['English'] = {
          title: content.title,
          description: content.description,
          thumbnail_url: content.thumbnail_url || '',
          thumbnail_file: null,
          thumbnail_preview: '', // Don't set preview initially, let useEffect fetch signed URL
          content_url: '',
          content_file_name: '',
          content_duration: '',
          sections: [{ section_title: '', section_description: '', content_url: '', content_file_name: '', content_duration: '' }]
        };
      }
      
      setLanguageContent(newLangContent);
      setActiveLangTabs(langTabs);
      setCurrentTab('English');
      
    } catch (error) {
      toast.error('Failed to fetch content details');
    }
  };

  // Initialize language content when adding new language
  const initLanguageContent = (lang) => {
    const englishContent = languageContent['English'] || {};
    return {
      title: '',
      description: '',
      thumbnail_url: englishContent.thumbnail_url || '', // Share thumbnail
      thumbnail_file: null,
      thumbnail_preview: englishContent.thumbnail_preview || '',
      content_url: '',
      content_file_name: '',
      content_duration: '',
      sections: englishContent.sections?.map(() => ({
        section_title: '',
        section_description: '',
        content_url: '',
        content_file_name: '',
        content_duration: ''
      })) || [{ section_title: '', section_description: '', content_url: '', content_file_name: '', content_duration: '' }]
    };
  };

  // Add new language tab
  const handleAddLanguage = () => {
    if (!newLangName.trim()) {
      toast.error('Please enter a language name');
      return;
    }
    
    const langName = newLangName.trim();
    if (activeLangTabs.includes(langName)) {
      toast.error(`${langName} tab already exists`);
      return;
    }
    
    setActiveLangTabs([...activeLangTabs, langName]);
    setLanguageContent({
      ...languageContent,
      [langName]: initLanguageContent(langName)
    });
    setCurrentTab(langName);
    setShowAddLangDialog(false);
    setNewLangName('');
    toast.success(`${langName} language tab added`);
  };

  // Remove language tab
  const handleRemoveLanguage = (lang) => {
    if (lang === 'English') {
      toast.error('English tab cannot be removed');
      return;
    }
    
    if (window.confirm(`Remove ${lang} tab? This will delete all content in this language.`)) {
      const newTabs = activeLangTabs.filter(t => t !== lang);
      const newContent = { ...languageContent };
      delete newContent[lang];
      
      setActiveLangTabs(newTabs);
      setLanguageContent(newContent);
      setCurrentTab('English');
      toast.success(`${lang} tab removed`);
    }
  };

  // Update content for current language
  const updateCurrentLangContent = (field, value) => {
    setLanguageContent(prev => ({
      ...prev,
      [currentTab]: {
        ...prev[currentTab],
        [field]: value
      }
    }));
  };

  // Update section for current language
  const updateCurrentLangSection = (sectionIndex, field, value) => {
    setLanguageContent(prev => {
      const currentContent = prev[currentTab];
      const newSections = [...currentContent.sections];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        [field]: value
      };
      return {
        ...prev,
        [currentTab]: {
          ...currentContent,
          sections: newSections
        }
      };
    });
  };

  // Add section (adds to all languages)
  const handleAddSection = () => {
    const newLangContent = { ...languageContent };
    Object.keys(newLangContent).forEach(lang => {
      newLangContent[lang].sections = [
        ...newLangContent[lang].sections,
        { section_title: '', section_description: '', content_url: '', content_file_name: '', content_duration: '' }
      ];
    });
    setLanguageContent(newLangContent);
    // Keep sectionMeta in sync
    setSectionMeta(prev => [...prev, { id: null, summary_video: null, summary_pdf: null, summary_duration: '' }]);
  };

  // Upload summary content (video or PDF) for a section
  const handleSummaryUpload = async (file, sectionIndex, summaryType) => {
    const meta = sectionMeta[sectionIndex];
    
    const fileContentType = summaryType === 'summary_video' ? 'video' : 'pdf';
    const uploadKey = `summary_${sectionIndex}_${summaryType}`;
    setSummaryUploading(prev => ({ ...prev, [uploadKey]: true }));
    
    try {
      await handleFileUpload(file, fileContentType, async (s3Key, fileName) => {
        // In edit mode with a saved section, persist to backend immediately
        if (meta?.id && contentId) {
          const token = localStorage.getItem('token');
          const summaryData = {};
          summaryData[summaryType] = {
            file_url: s3Key,
            file_name: fileName,
            language: 'English'
          };
          
          await axios.put(
            `${API}/training/content/${contentId}/sections/${meta.id}/summary`,
            summaryData,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        // Update local state (works for both add and edit mode)
        const newMeta = [...sectionMeta];
        if (!newMeta[sectionIndex]) {
          newMeta[sectionIndex] = { id: null, summary_video: null, summary_pdf: null };
        }
        newMeta[sectionIndex] = {
          ...newMeta[sectionIndex],
          [summaryType]: { file_url: s3Key, file_type: fileContentType, file_name: fileName, language: 'English' }
        };
        setSectionMeta(newMeta);
        toast.success(`Summary ${fileContentType} uploaded successfully`);
      });
    } catch (error) {
      console.error('Summary upload error:', error);
    } finally {
      setSummaryUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Remove summary content for a section
  const handleRemoveSummary = async (sectionIndex, summaryType) => {
    const meta = sectionMeta[sectionIndex];
    
    // In edit mode with a saved section, persist removal to backend
    if (meta?.id && contentId) {
      try {
        const token = localStorage.getItem('token');
        const removeData = {};
        removeData[summaryType === 'summary_video' ? 'remove_summary_video' : 'remove_summary_pdf'] = true;
        
        await axios.put(
          `${API}/training/content/${contentId}/sections/${meta.id}/summary`,
          removeData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        toast.error('Failed to remove summary content');
        return;
      }
    }
    
    // Update local state (works for both add and edit mode)
    const newMeta = [...sectionMeta];
    if (newMeta[sectionIndex]) {
      newMeta[sectionIndex] = { ...newMeta[sectionIndex], [summaryType]: null };
    }
    setSectionMeta(newMeta);
    toast.success('Summary content removed');
  };

  // Remove section (removes from all languages)
  const handleRemoveSection = (index) => {
    const currentSections = languageContent[currentTab].sections;
    if (currentSections.length <= 1) {
      toast.error('At least one chapter is required');
      return;
    }
    
    const newLangContent = { ...languageContent };
    Object.keys(newLangContent).forEach(lang => {
      newLangContent[lang].sections = newLangContent[lang].sections.filter((_, i) => i !== index);
    });
    setLanguageContent(newLangContent);
    // Keep sectionMeta in sync
    setSectionMeta(prev => prev.filter((_, i) => i !== index));
  };

  // File name validation function
  const validateFileName = (fileName, contentType) => {
    // Get filename without extension
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
    
    // Check for special characters
    // Allow: letters, numbers, spaces, underscore, and hyphen
    const validPattern = /^[a-zA-Z0-9_ -]+$/;
    if (!validPattern.test(nameWithoutExt)) {
      return {
        valid: false,
        error: 'File name contains invalid characters. Only letters, numbers, spaces, hyphens, and underscores are allowed.'
      };
    }
    
    // Check for valid extension
    const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    const allowedExtensions = {
      'video': ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'],
      'pdf': ['.pdf'],
      'image': ['.jpg', '.jpeg', '.png', '.gif', '.webp']
    };
    
    const contentTypeExtensions = allowedExtensions[contentType] || [];
    if (!extension || !contentTypeExtensions.includes(extension)) {
      return {
        valid: false,
        error: `Invalid file extension. Only supported file formats are allowed (for example: ${contentTypeExtensions.join(', ')}).`
      };
    }
    
    return { valid: true };
  };

  // File upload handler - uploads to S3
  const handleFileUpload = async (file, fileContentType, onSuccess) => {
    if (!file) {
      throw new Error('No file provided');
    }

    // File name validation
    const fileNameValidation = validateFileName(file.name, fileContentType);
    if (!fileNameValidation.valid) {
      toast.error(fileNameValidation.error);
      throw new Error(fileNameValidation.error);
    }

    // File size validation
    const MAX_THUMBNAIL_SIZE = 5 * 1024 * 1024; // 5 MB
    const MAX_CONTENT_SIZE = 200 * 1024 * 1024; // 200 MB
    
    if (fileContentType === 'image') {
      if (file.size > MAX_THUMBNAIL_SIZE) {
        const error = 'File size is larger than allowed. Please upload a thumbnail image up to 5 MB.';
        toast.error(error);
        throw new Error(error);
      }
    } else {
      if (file.size > MAX_CONTENT_SIZE) {
        const error = 'File size is larger than allowed. Please upload course topic content up to 200 MB.';
        toast.error(error);
        throw new Error(error);
      }
    }

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    // Determine folder based on content type
    const folder = fileContentType === 'image' ? 'thumbnails' : 'training-content';
    uploadFormData.append('folder', folder);

    setUploading(true);
    const uploadKey = `${file.name}-${Date.now()}`;
    setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
    
    // Show upload started message
    toast.info(`Uploading ${file.name}...`, { duration: 2000 });

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      // Upload to S3 via new endpoint
      // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
      const response = await axios.post(
        `${API}/s3/upload`,
        uploadFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [uploadKey]: percentCompleted }));
          },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          timeout: 300000 // 5 minutes timeout for large files
        }
      );

      if (response.data.status === 'success') {
        // Store S3 key and generated filename (random + extension)
        // The signed URL will be generated when viewing/displaying content
        console.log('Upload response successful:', response.data);
        onSuccess(response.data.s3_key, response.data.file_name || file.name);
        // For thumbnails, also set a preview via signed URL
        if (fileContentType === 'image') {
          try {
            const token = localStorage.getItem('token');
            const signedResp = await axios.post(
              `${API}/s3/signed-url`,
              { s3_key: response.data.s3_key, expiration: 3600 },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (signedResp.data?.signed_url) {
              updateCurrentLangContent('thumbnail_preview', signedResp.data.signed_url);
            }
          } catch (e) {
            // Preview fetch failure should not block upload; log quietly
            console.warn('Thumbnail signed URL fetch failed', e);
          }
        }
        toast.success(`Uploaded: ${file.name}`);
        return response.data.s3_key;
      } else {
        throw new Error('Upload failed: Invalid response from server');
      }
    } catch (error) {
      let errorMsg = 'Upload failed';
      
      if (error.response?.status === 413 || error.code === 'ECONNABORTED') {
        // Prefer server-provided detail when available; only fall back to size text
        errorMsg = error.response?.data?.detail
          || (fileContentType === 'image'
            ? 'Upload failed (HTTP 413). Thumbnail limit is 5 MB.'
            : 'Upload failed (HTTP 413). Content limit is 200 MB.');
        console.error('Upload failed - HTTP 413/timeout. File size (bytes):', file.size);
      } else if (error.response?.status === 401) {
        errorMsg = 'Your session has expired. Please log in again.';
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setTimeout(() => navigate('/signin'), 2000);
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
        if (errorMsg.includes('S3 service not configured')) {
          errorMsg = 'AWS S3 not configured. Please configure in Integration Settings.';
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      console.error('Upload error:', error);
      toast.error(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setUploading(false);
      setUploadProgress(prev => {
        const updated = { ...prev };
        delete updated[uploadKey];
        return updated;
      });
    }
  };

  // Submit handler
  const handleSubmit = async (isDraft = false) => {
    const englishContent = languageContent['English'];
    
    if (!englishContent?.title?.trim()) {
      toast.error('English title is required');
      setCurrentTab('English');
      return;
    }
    
    // Description required only for online and hybrid types
    if (formData.training_type !== 'offline' && !englishContent?.description?.trim()) {
      toast.error('English description is required');
      setCurrentTab('English');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Your session has expired. Please log in again.');
        navigate('/signin');
        return;
      }
      
      // Build title translations
      const titleTranslations = {};
      activeLangTabs.forEach(lang => {
        if (languageContent[lang]?.title) {
          titleTranslations[lang] = languageContent[lang].title;
        }
      });

      const payload = {
        title: englishContent.title,
        title_translations: titleTranslations,
        description: englishContent.description || '',
        thumbnail_url: englishContent.thumbnail_url || '',
        content_structure: formData.content_structure,
        content_type: formData.content_type,
        training_type: formData.training_type,
        status: isDraft ? 'draft' : 'published'
      };

      // Only include content for online type
      if (formData.training_type === 'online') {
        if (formData.content_structure === 'standalone') {
          // Build language versions for standalone
          payload.language_versions = activeLangTabs
            .filter(lang => languageContent[lang]?.content_url || languageContent[lang]?.content_file_name)
            .map(lang => ({
              language: lang,
              file_url: languageContent[lang].content_url || languageContent[lang].content_file_name,
              file_type: formData.content_type,
              file_name: languageContent[lang].content_file_name || languageContent[lang].content_url,
              ...(languageContent[lang].content_duration ? { content_duration: parseInt(languageContent[lang].content_duration) } : {})
            }));
          payload.sections = [];
        } else {
          // Build sections with language versions
          const sectionCount = englishContent.sections.length;
          payload.sections = [];
          
          for (let i = 0; i < sectionCount; i++) {
            const sectionTitleTranslations = {};
            const languageVersions = [];
            
            activeLangTabs.forEach(lang => {
              const langSection = languageContent[lang]?.sections?.[i];
              if (langSection?.section_title) {
                sectionTitleTranslations[lang] = langSection.section_title;
              }
              if (langSection?.content_url || langSection?.content_file_name) {
                languageVersions.push({
                  language: lang,
                  file_url: langSection.content_url || langSection.content_file_name,
                  file_type: formData.content_type,
                  file_name: langSection.content_file_name || langSection.content_url,
                  ...(langSection.content_duration ? { content_duration: parseInt(langSection.content_duration) } : {})
                });
              }
            });
            
            payload.sections.push({
              ...(sectionMeta[i]?.id ? { id: sectionMeta[i].id } : {}),
              section_title: englishContent.sections[i].section_title,
              section_title_translations: sectionTitleTranslations,
              section_description: englishContent.sections[i].section_description || '',
              content_type: formData.content_type,
              content_duration: englishContent.sections[i].content_duration ? parseInt(englishContent.sections[i].content_duration) : undefined,
              language_versions: languageVersions,
              ...(sectionMeta[i]?.summary_video ? { summary_video: { ...sectionMeta[i].summary_video, ...(sectionMeta[i].summary_duration ? { summary_duration: parseInt(sectionMeta[i].summary_duration) } : {}) } } : {}),
              ...(sectionMeta[i]?.summary_pdf ? { summary_pdf: { ...sectionMeta[i].summary_pdf, ...(sectionMeta[i].summary_duration ? { summary_duration: parseInt(sectionMeta[i].summary_duration) } : {}) } } : {})
            });
          }
          payload.language_versions = [];
        }
      } else {
        // Offline and Hybrid don't have content uploads
        payload.language_versions = [];
        payload.sections = [];
      }

      if (isEdit) {
        await axios.put(`${API}/training/content/${contentId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Training topic updated successfully');
      } else {
        await axios.post(`${API}/training/content`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Training topic created successfully');
      }
      navigate('/training');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setTimeout(() => navigate('/signin'), 2000);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save content');
      }
    } finally {
      setLoading(false);
    }
  };

  const currentContent = languageContent[currentTab] || {};
  const currentSections = currentContent.sections || [];

  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'EDIT TRAINING TOPIC' : 'ADD TRAINING TOPIC'}
          </h1>
          <Button onClick={() => navigate('/training')} variant="outline" className="border-[#0056B3] text-[#0056B3]">
            Back to Library
          </Button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Language Tabs */}
          <div className="border-b bg-gray-50">
            <div className="flex items-center overflow-x-auto">
              {activeLangTabs.map((lang) => (
                <div
                  key={lang}
                  className={`relative flex items-center px-6 py-4 cursor-pointer border-b-2 transition-colors whitespace-nowrap ${
                    currentTab === lang
                      ? 'border-[#0056B3] bg-white text-[#0056B3] font-semibold'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  onClick={() => setCurrentTab(lang)}
                >
                  <span>{lang}</span>
                  {lang !== 'English' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveLanguage(lang);
                      }}
                      className="ml-2 p-1 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setShowAddLangDialog(true)}
                className="flex items-center px-6 py-4 text-[#0056B3] hover:bg-blue-50 whitespace-nowrap font-medium"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Language
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6 md:p-8">
            {/* Training Type Selector - Show only on English tab and NOT for Exlva Admin roles */}
            {currentTab === 'English' && !isExlvaAdmin && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="text-base font-semibold mb-3 block">Training Type <span className="text-red-500">*</span></Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {trainingTypes.map((type) => (
                    <div
                      key={type.value}
                      onClick={() => setFormData({ ...formData, training_type: type.value })}
                      className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                        formData.training_type === type.value
                          ? 'border-[#0056B3] bg-white shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          formData.training_type === type.value ? 'border-[#0056B3]' : 'border-gray-300'
                        }`}>
                          {formData.training_type === type.value && (
                            <div className="w-2 h-2 rounded-full bg-[#0056B3]" />
                          )}
                        </div>
                        <span className={`font-semibold ${formData.training_type === type.value ? 'text-[#0056B3]' : 'text-gray-700'}`}>
                          {type.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 ml-7">{type.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Topic Title */}
            <div className="mb-6">
              <Label className="text-base font-semibold">
                Topic Title {currentTab === 'English' && <span className="text-red-500">*</span>}
              </Label>
              <Input
                value={currentContent.title || ''}
                onChange={(e) => updateCurrentLangContent('title', e.target.value)}
                placeholder={`Enter topic title in ${currentTab}`}
                className="mt-2"
              />
            </div>

            {/* Description - Show for Online and Hybrid */}
            {(formData.training_type === 'online' || formData.training_type === 'hybrid') && (
            <div className="mb-6 p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
              <Label className="text-base font-semibold text-gray-900 mb-2 block">
                Topic Content Description {currentTab === 'English' && <span className="text-red-500">*</span>}
              </Label>
              <p className="text-sm text-gray-600 mb-3">Provide a clear description of what this training chapter covers.</p>
              <Textarea
                value={currentContent.description || ''}
                onChange={(e) => updateCurrentLangContent('description', e.target.value)}
                placeholder={`Enter description in ${currentTab}`}
                rows={4}
                className="w-full"
              />
            </div>
            )}

            {/* Offline Training Notice */}
            {formData.training_type === 'offline' && currentTab === 'English' && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-600 text-lg">📋</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800">Offline Training Chapter</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      This chapter is for in-person training. Staff members will be marked as complete when their 
                      Reporting Manager marks their attendance as "Present".
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Hybrid Training Notice */}
            {formData.training_type === 'hybrid' && currentTab === 'English' && (
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-600 text-lg">🎯</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-800">Hybrid Training Chapter</h4>
                    <p className="text-sm text-purple-700 mt-1">
                      This chapter combines in-person attendance with online assessment. Staff must be marked 
                      "Present" by their Reporting Manager AND pass the linked assessment to complete this chapter.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Thumbnail - Show only on English tab and for Online/Hybrid types */}
            {currentTab === 'English' && formData.training_type !== 'offline' && (
              <div className="mb-6 p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold text-gray-900">Thumbnail Image</Label>
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                    Max: 5 MB
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">Upload a thumbnail image. Maximum allowed file size for thumbnail: <strong className="text-gray-900">5 MB</strong>. File upload is required - URL input is not available.</p>
                <p className="text-sm text-amber-600 mb-4 bg-amber-50 border border-amber-200 rounded-md p-2">
                  <strong>File name requirements:</strong> File name can contain letters, numbers, spaces, hyphens, and underscores. Only supported file formats are allowed (for example: .jpg, .png, .gif, .webp).
                </p>
                
                <div className="space-y-4">
                  {/* Thumbnail Preview/Upload */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Upload Thumbnail</Label>
                    {currentContent.thumbnail_preview || currentContent.thumbnail_url ? (
                      <div className="relative inline-block">
                        <img
                          src={currentContent.thumbnail_preview || currentContent.thumbnail_url}
                          alt="Thumbnail"
                          className="w-48 h-32 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            updateCurrentLangContent('thumbnail_url', '');
                            updateCurrentLangContent('thumbnail_preview', '');
                            updateCurrentLangContent('thumbnail_file', null);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-md transition-colors z-10"
                          data-tooltip="Remove thumbnail"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer inline-block">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              try {
                                // Show local preview immediately
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  updateCurrentLangContent('thumbnail_preview', reader.result);
                                };
                                reader.readAsDataURL(file);
                                
                                // Upload to S3
                                toast.info(`Uploading thumbnail: ${file.name}...`);
                                await handleFileUpload(file, 'image', (s3Key, fileName) => {
                                  console.log('Thumbnail uploaded successfully:', s3Key, fileName);
                                  updateCurrentLangContent('thumbnail_url', s3Key);
                                  updateCurrentLangContent('thumbnail_file_name', fileName);
                                });
                              } catch (error) {
                                // Error already shown in handleFileUpload via toast
                                console.error('Thumbnail upload error:', error);
                                // Reset the file input so user can try again
                                e.target.value = '';
                                // Clear the preview
                                updateCurrentLangContent('thumbnail_preview', '');
                              }
                            }
                          }}
                        />
                        <div className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex flex-col items-center justify-center">
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600 font-medium">Click to upload</span>
                          <span className="text-xs text-gray-500 mt-1">Image file</span>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Content Structure & Type - Show only on English tab and for Online type */}
            {currentTab === 'English' && formData.training_type === 'online' && (
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="text-base font-semibold">Content Structure <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.content_structure}
                    onValueChange={(value) => setFormData({ ...formData, content_structure: value })}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standalone">Standalone</SelectItem>
                      <SelectItem value="sectioned">Topic with Chapters</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-base font-semibold">Content File Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.content_type}
                    onValueChange={(value) => setFormData({ ...formData, content_type: value })}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="pdf">PDF</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Applies to all chapters in this chapter</p>
                </div>
              </div>
            )}

            {/* Standalone Content - Only for Online type */}
            {formData.training_type === 'online' && formData.content_structure === 'standalone' && (
              <div className="mb-6 p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold text-gray-900">
                    Topic Content / Video ({currentTab})
                  </Label>
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                    Max: 200 MB
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">Upload your {formData.content_type} file. Maximum allowed file size for course topic content: <strong className="text-gray-900">200 MB</strong>. File upload is required - URL input is not available.</p>
                <p className="text-sm text-amber-600 mb-4 bg-amber-50 border border-amber-200 rounded-md p-2">
                  <strong>File name requirements:</strong> File name can contain letters, numbers, spaces, hyphens, and underscores. Only supported file formats are allowed (for example: {formData.content_type === 'video' ? '.mp4' : '.pdf'}).
                </p>
                <ContentUploadField
                  contentType={formData.content_type}
                  fileUrl={currentContent.content_url}
                  fileName={currentContent.content_file_name}
                  onFileUpload={(url, name) => {
                    console.log('onFileUpload called with:', url, name);
                    updateCurrentLangContent('content_url', url);
                    updateCurrentLangContent('content_file_name', name);
                  }}
                  uploading={uploading}
                  handleFileUpload={handleFileUpload}
                />
                <div className="mt-4">
                  <Label className="text-sm font-medium text-gray-900">Content Duration (minutes) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="Duration in minutes"
                    value={currentContent.content_duration || ''}
                    onChange={(e) => updateCurrentLangContent('content_duration', e.target.value)}
                    className="mt-1 w-40"
                  />
                </div>
              </div>
            )}

            {/* Subchapter-based Content - Only for Online type */}
            {formData.training_type === 'online' && formData.content_structure === 'sectioned' && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-base font-semibold">Chapters</Label>
                  {currentTab === 'English' && (
                    <Button type="button" onClick={handleAddSection} variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-1" /> Add Chapter
                    </Button>
                  )}
                </div>
                
                <div className="space-y-4">
                  {currentSections.map((section, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium text-gray-900">Chapter {index + 1}</h4>
                        {currentTab === 'English' && currentSections.length > 1 && (
                          <Button
                            type="button"
                            onClick={() => handleRemoveSection(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Chapter Title */}
                      <div className="mb-3">
                        <Label className="text-sm">Chapter Title</Label>
                        <Input
                          value={section.section_title || ''}
                          onChange={(e) => updateCurrentLangSection(index, 'section_title', e.target.value)}
                          placeholder={`Chapter title in ${currentTab}`}
                          className="mt-1"
                        />
                      </div>
                      
                      {/* Chapter Description - Only on English */}
                      {currentTab === 'English' && (
                        <div className="mb-3">
                          <Label className="text-sm">Chapter Description</Label>
                          <Textarea
                            value={section.section_description || ''}
                            onChange={(e) => updateCurrentLangSection(index, 'section_description', e.target.value)}
                            placeholder="Chapter description (optional)"
                            rows={2}
                            className="mt-1"
                          />
                        </div>
                      )}
                      
                      {/* Subchapter Content */}
                      <div className="pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm font-medium text-gray-900">Content ({currentTab})</Label>
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                            Max: 200 MB
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mb-3">Maximum allowed file size for course topic content: <strong className="text-gray-900">200 MB</strong></p>
                        <p className="text-xs text-amber-600 mb-3 bg-amber-50 border border-amber-200 rounded-md p-2">
                          <strong>File name requirements:</strong> File name can contain letters, numbers, spaces, hyphens, and underscores. Only supported file formats are allowed (for example: {formData.content_type === 'video' ? '.mp4' : '.pdf'}).
                        </p>
                        <ContentUploadField
                          contentType={formData.content_type}
                          fileUrl={section.content_url}
                          fileName={section.content_file_name}
                          onFileUpload={(url, name) => {
                            updateCurrentLangSection(index, 'content_url', url);
                            updateCurrentLangSection(index, 'content_file_name', name);
                          }}
                          uploading={uploading}
                          handleFileUpload={handleFileUpload}
                          compact
                        />
                        <div className="mt-3">
                          <Label className="text-sm font-medium text-gray-900">Content Duration (minutes) <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            min="1"
                            placeholder="Duration in minutes"
                            value={section.content_duration || ''}
                            onChange={(e) => updateCurrentLangSection(index, 'content_duration', e.target.value)}
                            className="mt-1 w-40"
                          />
                        </div>
                      </div>

                      {/* Summary Content Upload - For Exlva Admin roles */}
                      {isExlvaAdmin && currentTab === 'English' && (
                        <div className="pt-3 mt-3 border-t border-dashed border-gray-200">
                          <Label className="text-sm font-medium text-gray-700 mb-2 block">
                            Summary Content <span className="text-xs font-normal text-gray-500">(Adaptive Learning)</span>
                          </Label>
                          <p className="text-xs text-gray-400 mb-2">Upload a summary video or PDF shown to learners who pass adaptive questions.</p>
                          {(() => {
                            const summaryVideo = sectionMeta[index]?.summary_video;
                            const summaryPdf = sectionMeta[index]?.summary_pdf;
                            const summaryData = summaryVideo || summaryPdf;
                            const isVideo = !!summaryVideo;
                            const summaryType = isVideo ? 'summary_video' : 'summary_pdf';
                            const isUploading = summaryUploading[`summary_${index}_summary_video`] || summaryUploading[`summary_${index}_summary_pdf`];

                            if (summaryData) {
                              return (
                                <div className="flex items-center gap-3 flex-wrap">
                                  <div className="flex items-center gap-2 text-green-600">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm font-medium">{summaryData.file_name || (isVideo ? 'Video uploaded' : 'PDF uploaded')}</span>
                                  </div>
                                  <Button
                                    type="button" variant="outline" size="sm"
                                    className="text-xs flex items-center gap-1"
                                    onClick={async () => {
                                      const previewWindow = window.open('', '_blank', 'width=900,height=650');
                                      if (!previewWindow) {
                                        toast.error('Popup blocked. Please allow popups to preview summary content.');
                                        return;
                                      }
                                      writePreviewWindowContent(previewWindow, '<html><head><title>Loading Preview</title></head><body style="font-family:Arial,sans-serif;padding:20px;">Loading preview...</body></html>');
                                      try {
                                        const previewSource = getPreviewSource(summaryData.file_url, summaryData.file_name);
                                        if (!previewSource) {
                                          throw new Error('Summary preview source is missing for this chapter.');
                                        }
                                        const previewUrl = await resolvePreviewUrl(previewSource);
                                        if (isVideo) {
                                          openVideoPreviewWindow(previewWindow, previewUrl, 'Summary Preview');
                                        } else {
                                          previewWindow.location.href = previewUrl;
                                        }
                                      } catch (error) {
                                        writePreviewError(previewWindow, 'Summary Preview Unavailable', error?.message || 'Failed to open summary preview.');
                                        console.error('Summary preview failed:', error);
                                      }
                                    }}
                                  >
                                    <Play className="w-3 h-3" />
                                    Preview {isVideo ? 'Video' : 'PDF'}
                                  </Button>
                                  <Button
                                    type="button" variant="ghost" size="sm"
                                    className="text-red-500 hover:text-red-700 h-7 px-2 text-xs"
                                    onClick={() => handleRemoveSummary(index, summaryType)}
                                  >
                                    <X className="w-3 h-3 mr-1" /> Remove
                                  </Button>
                                </div>
                              );
                            }

                            return (
                              <label className="block">
                                <input
                                  type="file"
                                  accept=".mp4,.webm,.ogg,.mov,.pdf"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const ext = file.name.split('.').pop().toLowerCase();
                                      const type = ext === 'pdf' ? 'summary_pdf' : 'summary_video';
                                      handleSummaryUpload(file, index, type);
                                    }
                                    e.target.value = '';
                                  }}
                                />
                                <div className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors ${isUploading ? 'bg-blue-50 border-blue-500 opacity-50 pointer-events-none' : 'bg-white'}`}>
                                  {isUploading ? (
                                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 text-gray-500" />
                                  )}
                                  <span className={`text-sm ${isUploading ? 'text-blue-600' : 'text-gray-700'} font-medium`}>
                                    {isUploading ? 'Uploading...' : 'Choose File'}
                                  </span>
                                </div>
                              </label>
                            );
                          })()}
                          {/* Summary Duration */}
                          {(sectionMeta[index]?.summary_video || sectionMeta[index]?.summary_pdf) && (
                            <div className="mt-3">
                              <Label className="text-sm font-medium text-gray-700">Summary Duration (minutes) <span className="text-red-500">*</span></Label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="Duration in minutes"
                                value={sectionMeta[index]?.summary_duration || ''}
                                onChange={(e) => {
                                  const newMeta = [...sectionMeta];
                                  newMeta[index] = { ...newMeta[index], summary_duration: e.target.value };
                                  setSectionMeta(newMeta);
                                }}
                                className="mt-1 w-40"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 md:px-8 py-4 bg-gray-50 border-t flex flex-col sm:flex-row justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/training')}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSubmit(true)}
              disabled={loading}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save as Draft
            </Button>
            <Button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className="bg-[#0056B3] hover:bg-[#0056B3]/90"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publish Topic
            </Button>
          </div>
        </div>

        {/* Add Language Dialog */}
        <Dialog open={showAddLangDialog} onOpenChange={setShowAddLangDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Language</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label>Language Name</Label>
              <Input
                value={newLangName}
                onChange={(e) => setNewLangName(e.target.value)}
                placeholder="Enter language name (e.g., Hindi, Kannada)"
                className="mt-2"
                onKeyDown={(e) => e.key === 'Enter' && handleAddLanguage()}
              />
              
              {/* Quick select suggestions */}
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">Quick select:</p>
                <div className="flex flex-wrap gap-2">
                  {availableLanguages
                    .filter(lang => !activeLangTabs.includes(lang))
                    .map(lang => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => setNewLangName(lang)}
                        className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                          newLangName === lang
                            ? 'bg-[#0056B3] text-white border-[#0056B3]'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#0056B3]'
                        }`}
                      >
                        {lang}
                      </button>
                    ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddLangDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddLanguage} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                Add Language
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

// Reusable Content Upload Field Component - File upload only (no URL input)
const ContentUploadField = ({
  contentType,
  fileUrl,
  fileName,
  onFileUpload,
  uploading,
  handleFileUpload,
  compact = false
}) => {
  const inputId = `file-upload-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <div className="space-y-4">
      {/* File Upload Subchapter */}
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">
          Upload {contentType === 'video' ? 'Video' : 'PDF'} File
        </Label>
        <input
          type="file"
          id={inputId}
          accept={contentType === 'video' ? 'video/*' : '.pdf'}
          onChange={async (e) => {
            const file = e.target.files[0];
            if (file) {
              console.log('File selected:', file.name, 'Type:', contentType);
              toast.info(`Selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
              try {
                await handleFileUpload(file, contentType, (s3Key, fileName) => {
                  console.log('Upload successful, calling onFileUpload with:', s3Key, fileName);
                  onFileUpload(s3Key, fileName);
                });
              } catch (error) {
                // Error already shown in handleFileUpload via toast
                console.error('File upload error:', error);
                // Reset the file input so user can try again
                e.target.value = '';
              }
            }
          }}
          className="hidden"
        />
        <label
          htmlFor={inputId}
          className={`cursor-pointer inline-flex items-center justify-center gap-2 px-6 ${compact ? 'py-2.5' : 'py-3'} border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors ${uploading ? 'bg-blue-50 border-blue-500' : 'bg-white'}`}
        >
          {uploading ? (
            <Loader2 className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-blue-500 animate-spin`} />
          ) : (
            <Upload className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-gray-500`} />
          )}
          <span className={`${compact ? 'text-sm' : 'text-base'} ${uploading ? 'text-blue-600' : 'text-gray-700'} font-medium`}>
            {uploading ? 'Uploading...' : `Choose ${contentType === 'video' ? 'Video' : 'PDF'} File`}
          </span>
        </label>
        {(fileUrl || fileName) && (
          <div className="mt-2 flex items-center gap-3">
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">{fileName || 'File uploaded successfully'}</span>
            </div>
            {contentType === 'video' && (fileUrl || fileName) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  const videoWindow = window.open('', '_blank', 'width=900,height=650');
                  if (!videoWindow) {
                    toast.error('Popup blocked. Please allow popups to preview videos.');
                    return;
                  }
                  writePreviewWindowContent(videoWindow, '<html><head><title>Loading Preview</title></head><body style="font-family:Arial,sans-serif;padding:20px;">Loading preview...</body></html>');
                  try {
                    const previewSource = getPreviewSource(fileUrl, fileName);
                    if (!previewSource) {
                      throw new Error('Main content preview source is missing for this chapter.');
                    }
                    const videoUrl = await resolvePreviewUrl(previewSource);
                    openVideoPreviewWindow(videoWindow, videoUrl, 'Video Preview');
                  } catch (error) {
                    writePreviewError(videoWindow, 'Main Content Preview Unavailable', error?.message || 'Failed to open main content preview.');
                    console.error('Main content preview failed:', error);
                  }
                }}
                className="text-xs flex items-center gap-1"
              >
                <Play className="w-3 h-3" />
                Preview Video
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddTrainingContentPage;
